<template>
  <div class="modal-backdrop" @click="handleBackdropClick">
    <div class="offerModal">

      <!-- Start - top logo section -->
      <div class="modalHeaderWrapper">
        <img class="partnerLogo" :src="offer.partner.logoImage || '/img/partner_logo_placeholder.png'" />
        <div class="is-flex-grow-1"><!-- stretchy horizontal spacer --></div>
        <i class="fas fa-times modalCloseButton" @click="closeClicked()"></i>
      </div>
      <!-- End - top logo section -->

      <!-- Start - content columns -->
      <div class="modalContentWrapper">

        <!-- Start - left column -->
        <div class="modalLeftColumn">

          <h1 class="offerName">{{ offer.name }}</h1>
          <h2 class="partnerName mt-2">
            by
            <a v-if="offer.partner.website" :href="offer.partner.website" target="_blank">{{ offer.partner.name }}</a>
            <span v-else>{{ offer.partner.name }}</span>
          </h2>

          <div v-if="offer.partner.address" class="pt-1 font-bold">
            <i class="fas fa-map-pin mr-2"></i>
            <strong>
              <a v-if="offer.partner.addressLink" :href="offer.partner.addressLink" target="_blank">{{ offer.partner.address }}</a>
              <span v-else>{{ offer.partner.address }}</span>
            </strong>
          </div>
          <div class="pt-4 pb-2 text-md text-secondary-300 modalTextContent" v-html="offer.moreInfo || offer.description"/>

          <div v-if="categoryTags.length != 0" class="mt-3">
            <h2 class="inline mr-2">Helps With:</h2>
            <OfferTag :tag="tag" type="CATEGORY" v-for="tag in categoryTags" :key="tag.id"/>
          </div>

          <div v-if="servingTags.length != 0" class="mt-3">
            <h2 class="inline mr-2">Serving:</h2>
            <OfferTag :tag="tag" type="SERVING" v-for="tag in servingTags" :key="tag.id"/>
          </div>

        </div>
        <!-- End - left column -->

        <div class="modalHorizontalSpacer"></div>

        <!-- Start - right column -->
        <div class="modalRightColumn">

          <h2>To be eligible for this program, you must meet the following requirements:</h2>

          <div class="mt-2 text-secondary-300 modalTextContent" v-html="offer.eligibility"></div>

          <div class="is-flex-grow-1"><!-- stretchy vertical spacer --></div>

          <h2 class="nextSteps mt-5">Next Steps</h2>

          <div class="mt-2 text-secondary-300 modalTextContent" v-html="offer.nextSteps"></div>

          <button v-if="offer.applyLink" @click="applyClicked()" class="modalApplyButton mt-4">
            {{offer.applyLabel ? offer.applyLabel.toUpperCase() : 'APPLY NOW'}}
          </button>

        </div>
        <!-- End - right column -->

      </div>
      <!-- End - content columns -->
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { PartnerOffer } from '@/features/partnerOffers/model';
import OfferTag from './OfferTag.vue';
import { logInteraction } from './api';
import { InteractionType } from '@/features/analytics/api';

const props = defineProps<{
  offer: PartnerOffer,
  context: string,
}>();

const servingTags = computed(()=>props.offer.tags.filter(t=>t.id.startsWith("serving::")));
const categoryTags = computed(()=>props.offer.tags.filter(t=>t.id.startsWith("category::")));

const didApply = ref<boolean>(false);

const emit = defineEmits<{
    (e: 'closed'): void
}>();

const handleBackdropClick = (e: any) => {
  if (e.target.className === 'modal-backdrop') {
    closeClicked();
  }
};

const applyClicked = () => {
  didApply.value = true;
  logInteraction(props.context, props.offer, InteractionType.PROGRESS, "applyClicked");
  window.open(props.offer.applyLink, "_blank");
}

const closeClicked = () => {
  if (!didApply.value) {
    logInteraction(props.context, props.offer, InteractionType.CANCELLATION, "closeClicked");
  }
  emit('closed');
}

</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

@keyframes fadeIn {
  from {opacity: 0;}
  to {opacity: 1;}
}

.modal-backdrop {
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);

  animation: fadeIn 0.2s;
  animation-fill-mode: forwards;
}

.offerModal {
  background: #FFFFFF;
  width: min(75%, 1100px);
  border-radius: 7px;
  text-align: left;

  padding: 1% 1.5% 1% 1.5%;
  display: flex;
  flex-direction: column;

  max-height: 95%;
}

.modalHeaderWrapper {
  display: flex;
  flex-direction: row;

  padding: 1rem 1rem 0.5rem 1rem;
}

.modalCloseButton {
  scale: 2;
  margin-top: 1.5rem;
  opacity: 0.5;
  cursor: pointer;
}

.partnerLogo {
  height: 5em;
  width: auto;
}

.offerName {
  font-size: 190%;
  line-height: 1.25;
}

.partnerName {
  font-size: 125%;
  font-weight: normal;
}

.nextSteps {
  font-size: 150%;
}

.inline { 
  display: inline;
}

.modalContentWrapper {
  display: flex;
  flex-direction: row;

  overflow-y: auto;
  padding: 0.5rem 1rem 1rem 1rem;
}

.modalHorizontalSpacer {
  width: 6vw;
}

.modalLeftColumn {
  width: 60%;
  display: flex;
  flex-direction: column;
}

.modalRightColumn {
  width: 40%;
  display: flex;
  flex-direction: column;
}

.modalApplyButton {
  width: 100%;
  padding-top: 0.75rem;
  padding-bottom: 0.75rem;

  color: white;
  font-weight: 700;
  font-size: 0.875rem;
  font-family: inherit;
  line-height: inherit;

  background-color: $primary;
  border-style: none;
  border-radius: 0.3rem;

  cursor: pointer;
}

/* On small screens, stack the modal vertically */
@media screen and (max-width: 800px) {
  .offerModal {
    width: 90%;
  }

  .modalContentWrapper {
    flex-direction: column;
  }

  .modalLeftColumn {
    width: 100%;
  }

  .modalRightColumn {
    padding-top: 2rem;
    width: 100%;
  }
}

a {
  text-decoration: underline;
}

.modalTextContent ul {
  margin-top: 0.5em;
  list-style: outside !important;
  padding-left: 1.4em;
}

.modalTextContent p:not(:last-of-type) {
  margin-bottom: 0.7em;
}

</style>
